import { useCallback, useMemo } from 'react';

import { useStoreContext } from 'features/store';

import {
  LoyaltyServiceServiceMode,
  useLoyaltyServiceAnonymousRewardsQuery,
  useLoyaltyServiceRewardsQuery,
} from 'generated/graphql-gateway';
import { IncentiveEvaluationMap } from 'state/loyalty/hooks/types';
import { useLoyaltyUserId } from 'state/loyalty/hooks/use-loyalty-user';
import { useServiceModeContext } from 'state/service-mode';

import { mapServiceRewardCategories } from '../utils';

const SERVICE_MODE_MAP: Record<string, LoyaltyServiceServiceMode | undefined> = {
  CURBSIDE: LoyaltyServiceServiceMode.CURBSIDE,
  DELIVERY: LoyaltyServiceServiceMode.DELIVERY,
  CATERING_DELIVERY: LoyaltyServiceServiceMode.DELIVERY,
  DRIVE_THRU: LoyaltyServiceServiceMode.DRIVE_THRU,
  MOBILE_ORDER_DRIVE_THRU: LoyaltyServiceServiceMode.DRIVE_THRU,
  EAT_IN: LoyaltyServiceServiceMode.EAT_IN,
  TABLE_SERVICE: LoyaltyServiceServiceMode.EAT_IN,
  TAKEOUT: LoyaltyServiceServiceMode.TAKEOUT,
  CATERING_PICKUP: LoyaltyServiceServiceMode.TAKEOUT,
};

export function useLoyaltyServiceRewards({ skip = false }: { skip?: boolean } = {}) {
  const { serviceMode } = useServiceModeContext();
  const { store } = useStoreContext();
  const storeId = store?.number;
  const { loyaltyUserId, loading: loyaltyUserIdLoading } = useLoyaltyUserId();

  const loyaltyServiceMode = serviceMode ? SERVICE_MODE_MAP[serviceMode] : undefined;

  const {
    data: loyaltyServiceRewardsData,
    loading: loyaltyServiceRewardsLoading,
    refetch: refetchLoyaltyServiceRewards,
  } = useLoyaltyServiceRewardsQuery({
    variables: {
      serviceMode: loyaltyServiceMode,
      storeId,
      omitInvalids: false,
    },
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
    skip: skip || loyaltyUserIdLoading || !loyaltyUserId,
  });

  const {
    data: loyaltyServiceAnonymousRewardsData,
    loading: loyaltyServiceAnonymousRewardsLoading,
    refetch: refetchLoyaltyServiceAnonymousRewards,
  } = useLoyaltyServiceAnonymousRewardsQuery({
    variables: {
      storeId,
    },
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
    skip: skip || loyaltyUserIdLoading || Boolean(loyaltyUserId),
  });

  const loading =
    loyaltyUserIdLoading ||
    (loyaltyUserId ? loyaltyServiceRewardsLoading : loyaltyServiceAnonymousRewardsLoading);

  const categories = useMemo(
    () =>
      (loyaltyUserId
        ? loyaltyServiceRewardsData?.loyaltyServiceRewards?.categories
        : loyaltyServiceAnonymousRewardsData?.loyaltyServiceAnonymousRewards?.categories) ?? [],
    [loyaltyServiceAnonymousRewardsData, loyaltyServiceRewardsData, loyaltyUserId]
  );

  const { rewardCategories, sanityRewardsMap, engineRewardsMap } = useMemo(() => {
    return mapServiceRewardCategories(categories);
  }, [categories]);

  /**
   * Derive a classic `IncentiveEvaluationMap` keyed by `loyaltyEngineId`
   * (the service reward `id`) from the per-reward `errors` the service
   * already returns. This keeps the legacy `LoyaltyRewardsList` happy
   * without making a second evaluation round-trip.
   */
  const rewardsEvaluationMap = useMemo<IncentiveEvaluationMap>(() => {
    const map: IncentiveEvaluationMap = {};
    for (const category of categories) {
      for (const reward of category.rewards) {
        if (reward.errors?.length) {
          map[reward.id] = reward.errors.map(
            ({ code, currentValue, message, ruleId, targetValue }) => ({
              code,
              currentValue,
              message,
              ruleId,
              targetValue,
            })
          );
        }
      }
    }
    return map;
  }, [categories]);

  const refreshRewards = useCallback(async () => {
    if (skip || loyaltyUserIdLoading) {
      return;
    }
    if (loyaltyUserId) {
      await refetchLoyaltyServiceRewards();
      return;
    }
    await refetchLoyaltyServiceAnonymousRewards();
  }, [
    loyaltyUserId,
    loyaltyUserIdLoading,
    refetchLoyaltyServiceAnonymousRewards,
    refetchLoyaltyServiceRewards,
    skip,
  ]);

  return {
    rewardCategories,
    sanityRewardsMap,
    engineRewardsMap,
    rewardsEvaluationMap,
    loading,
    refreshRewards,
  };
}
